import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { T2dErrorPageComponent } from '@tec2date/ui-components';
import { DefaultLayoutComponent } from './layout/default-layout/default-layout.component';
import { LoginPageComponent } from './views/login/login-page.component';
import { authGuard } from './guards/auth.guard';
import { systemAdminGuard, mandantAdminGuard, kuratorOderAdminGuard } from './guards/role.guard';
import { AuthService } from './services/auth.service';

export const routes: Routes = [
  { path: 'login', component: LoginPageComponent },
  {
    // Step 2 des Two-Step-Logins (TOTP-Code-Eingabe). Erreicht über
    // LoginPage, wenn das Backend bei aktivem TOTP einen Challenge-Token
    // zurückgibt.
    path: 'login/2fa',
    loadComponent: () =>
      import('./views/login/two-factor-verify-page.component').then((m) => m.TwoFactorVerifyPageComponent),
  },
  {
    // SSO-Einstieg: User gibt Firmen-Email ein, Backend resolved zur
    // Authorize-URL und redirected zum IdP.
    path: 'login/sso',
    loadComponent: () => import('./views/login/sso-entry-page.component').then((m) => m.SsoEntryPageComponent),
  },
  {
    // Magic-Link-Anforderung: User gibt Email ein, bekommt Login-Link per Mail.
    path: 'login/magic-link',
    loadComponent: () => import('./views/login/magic-link-request.component').then((m) => m.MagicLinkRequestComponent),
  },
  {
    // Öffentliche Accept-Seite für Einladungs-Links. Kein authGuard — der
    // Token selbst berechtigt den Zugriff.
    path: 'einladung/:token',
    loadComponent: () =>
      import('./views/invitation/accept-invitation.component').then((m) => m.AcceptInvitationComponent),
  },
  {
    // Zwangs-Passwort-ändern — muss außerhalb des geschützten Layouts liegen,
    // damit der Guard bei mustChangePassword=true hierher redirecten kann ohne
    // endlose Schleife. Keine canActivate-Guard hier.
    path: 'passwort-aendern',
    loadComponent: () => import('./views/auth/passwort-aendern.component').then((m) => m.PasswortAendernComponent),
  },
  {
    // Pflicht-Passkey-Registrierung für Break-Glass-Konten — muss außerhalb des
    // geschützten Layouts liegen, damit der Guard bei breakGlass=true hierher
    // redirecten kann ohne endlose Schleife. Keine canActivate-Guard hier.
    path: 'passkey-einrichten',
    loadComponent: () => import('./views/auth/passkey-einrichten.component').then((m) => m.PasskeyEinrichtenComponent),
  },
  {
    // Passwort vergessen — Email-Eingabe für Reset-Link.
    path: 'password-vergessen',
    loadComponent: () => import('./views/auth/password-vergessen.component').then((m) => m.PasswordVergessenComponent),
  },
  {
    // Reset-Ziel-Seite mit Token aus der Mail.
    path: 'password-reset/:token',
    loadComponent: () => import('./views/auth/password-reset.component').then((m) => m.PasswordResetComponent),
  },
  {
    // Externe Stundennachweis-Erfassung (SB, T9) — accountloser Token+OTP-Flow
    // außerhalb des Portal-Layouts, kein authGuard (der Token/OTP berechtigt).
    path: 'stundennachweis/:token',
    loadComponent: () =>
      import('./views/extern/stundennachweis-extern.component').then((m) => m.StundennachweisExternComponent),
  },
  {
    // Prüfanleitung für die OCR-Chunk-Prüfung, aus dem Prüfmodal heraus verlinkt. Kein
    // eigener Guard: sie enthält keine Daten, nur Arbeitsanweisungen — und sie muss auch dann
    // erreichbar sein, wenn ein Prüfer sie in einem zweiten Tab offen halten will.
    path: 'hilfe/ocr-pruefanleitung',
    loadComponent: () =>
      import('./views/dms/pages/hilfe/ocr-pruefanleitung.component').then((m) => m.OcrPruefanleitungComponent),
  },
  {
    // Öffentliche Open-Source-Hinweise-Seite (PDF-Plattform Welle 3,
    // Aufgabe 15) — kein authGuard: der SIL-OFL-1.1-Lizenzhinweis für die
    // eingebetteten PDF-Fonts muss ohne Anmeldung lesbar sein, auch für
    // Empfänger von PLANAUDIT-Dokumenten ohne eigenen Account.
    path: 'open-source-hinweise',
    loadComponent: () =>
      import('./views/oeffentlich/open-source-hinweise.component').then((m) => m.OpenSourceHinweiseComponent),
  },
  {
    // Dedizierte 403-Seite (Guard-Redirect-Ziel). Die frühere
    // data:{errorCode:403}-Variante band ohne withComponentInputBinding()
    // nie an den @Input() — es erschien der 404-Default.
    path: '403',
    loadComponent: () => import('./views/pages/page403/page403.component').then((m) => m.Page403Component),
  },
  { path: '404', component: T2dErrorPageComponent, data: { errorCode: 404 } },
  {
    // Dedizierte 500-Seite — gleiche Bug-Klasse wie '403': data:{errorCode:500}
    // band nie an den @Input(), es erschien der 404-Default.
    path: '500',
    loadComponent: () => import('./views/pages/page500/page500.component').then((m) => m.Page500Component),
  },
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () => import('./views/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        // Team-Seite „Unser KI-Team" — für ALLE eingeloggten Nutzer
        // (nur der authGuard der Eltern-Route, bewusst KEIN systemAdminGuard).
        path: 'ki-team',
        loadComponent: () => import('./views/ki-team/ki-team.component').then((m) => m.KiTeamComponent),
      },
      {
        // Öffentlicher Team-Steckbrief (Spec §11a Punkt 4) — ebenfalls für
        // ALLE eingeloggten Nutzer (kein Admin-Guard); nur AKTIVE Personas,
        // Entwürfe liefern Backend-seitig 404 → Toast + Redirect zu /ki-team.
        path: 'ki-team/:personaId',
        loadComponent: () =>
          import('./views/ki-team/ki-team-steckbrief.component').then((m) => m.KiTeamSteckbriefComponent),
      },
      {
        // Flache Redirect-Route für DMS-Verknüpfungen zu Stundennachweisen
        // (neunter Link-Typ, Welle 3 Aufgabe 9): die echte Detailroute liegt
        // projekt- UND vertragsverschachtelt (`projekte/:id/vertraege/:vertragId/
        // stundenabrechnung/:nachweisId`) — hier wird projektId/vertragId per
        // API aufgelöst (B-1) und dorthin weitergeleitet.
        path: 'stundennachweise/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./views/portal/vertraege/stundenabrechnung/stundennachweis-redirect.component').then(
            (m) => m.StundennachweisRedirectComponent,
          ),
      },
      // Werkzeuge — für ALLE eingeloggten Nutzer (kein zusätzlicher Guard,
      // authGuard der Eltern-Route greift).
      {
        path: 'werkzeuge',
        children: [
          { path: '', redirectTo: 'nhk-rechner', pathMatch: 'full' },
          {
            path: 'nhk-rechner',
            loadComponent: () =>
              import('./views/werkzeuge/werkzeuge-page.component').then((m) => m.WerkzeugePageComponent),
          },
        ],
      },
      {
        path: 'meine-einladungen',
        loadComponent: () =>
          import('./views/invitation/meine-einladungen.component').then((m) => m.MeineEinladungenComponent),
      },
      {
        path: 'profil',
        loadComponent: () => import('./views/profil/profil-overview.component').then((m) => m.ProfilOverviewComponent),
      },
      {
        path: 'profil/mein-profil',
        loadComponent: () => import('./views/profile/mein-profil.component').then((m) => m.MeinProfilComponent),
      },
      {
        // Sicherheits-Seite: 2FA verwalten, später auch Passkeys/API-Tokens.
        path: 'profil/sicherheit',
        loadComponent: () => import('./views/profile/sicherheit.component').then((m) => m.SicherheitComponent),
      },
      {
        // Self-Sicht des Auth-Audit-Logs (eigene Events).
        path: 'profil/sicherheit/aktivitaet',
        data: { mode: 'self' },
        loadComponent: () => import('./views/audit/audit-log.component').then((m) => m.AuditLogComponent),
      },
      {
        // Personal Access Tokens für Maschinen-Zugriffe.
        path: 'profil/api-tokens',
        loadComponent: () => import('./views/profile/api-tokens.component').then((m) => m.ApiTokensComponent),
      },
      {
        // Admin-Sicht des Auth-Audit-Logs.
        // Mandant-Admin sieht eigenen Mandanten, System-Admin alle.
        path: 'organisationseinstellungen/audit',
        canActivate: [mandantAdminGuard],
        data: { mode: 'admin' },
        loadComponent: () => import('./views/audit/audit-log.component').then((m) => m.AuditLogComponent),
      },
      {
        // Redirect-Route des Sidebar-Mandant-Switchers.
        // canActivate setzt den aktiven Mandanten und gibt einen UrlTree zum
        // Dashboard zurück — die Component wird nie geladen/instanziiert.
        // Navigation zum Dashboard (statt zurück zur vorherigen URL), damit
        // keine Stale-Detail-Seiten mit Mandant-fremder ID hängenbleiben
        // (Plan-Entscheidung aus Session 109).
        path: 'mandant-wechsel/:id',
        loadComponent: () => import('./views/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [
          (route: any) => {
            const id = route.paramMap.get('id');
            if (id) {
              inject(AuthService).setActiveMandant(id);
            }
            return inject(Router).parseUrl('/dashboard');
          },
        ],
      },
      // Systemeinstellungen (SystemAdmin) — Overview
      {
        path: 'systemeinstellungen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/systemeinstellungen-overview.component').then(
            (m) => m.SystemeinstellungenOverviewComponent,
          ),
      },
      // Organisationseinstellungen (MandantAdmin) — Overview
      {
        path: 'organisationseinstellungen',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/organisationseinstellungen-overview.component').then(
            (m) => m.OrganisationseinstellungenOverviewComponent,
          ),
      },
      // Organisationseinstellungen — DMS-Konfiguration Übersicht (Parent-Navigationsseite)
      {
        path: 'organisationseinstellungen/dms-konfiguration',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/dms-konfiguration/dms-konfiguration-overview.component').then(
            (m) => m.DmsKonfigurationOverviewComponent,
          ),
      },
      // Systemeinstellungen — Sub-Routes
      {
        path: 'systemeinstellungen/smtp-config',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/smtp-config/smtp-config.component').then((m) => m.SmtpConfigComponent),
      },
      {
        path: 'systemeinstellungen/email-templates',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/email-templates/email-templates-list.component').then(
            (m) => m.EmailTemplatesListComponent,
          ),
      },
      {
        // SSO-Konfiguration für den eigenen Mandanten (Phase 3).
        path: 'organisationseinstellungen/sso',
        canActivate: [mandantAdminGuard],
        loadComponent: () => import('./views/admin/sso/sso-config.component').then((m) => m.SsoConfigComponent),
      },
      {
        // Auth-Policy für den eigenen Mandanten (welche Methoden erlaubt/Pflicht).
        path: 'organisationseinstellungen/auth-policy',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/auth-policy/auth-policy.component').then((m) => m.AuthPolicyComponent),
      },
      {
        // DMS-Speicher-Status + Connection-Test + Quota-Antrag.
        path: 'organisationseinstellungen/speicher',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/speicher/speicher-page.component').then(
            (m) => m.SpeicherPageComponent,
          ),
      },
      {
        // DMS-Verschlüsselung-at-Rest-Status.
        path: 'organisationseinstellungen/verschluesselung',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/verschluesselung/verschluesselung-page.component').then(
            (m) => m.VerschluesselungPageComponent,
          ),
      },
      {
        // DMS-TSA (Zeitstempel-Authority)-Konfiguration.
        path: 'organisationseinstellungen/tsa',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/tsa/tsa-page.component').then((m) => m.TsaPageComponent),
      },
      {
        // Branding der PDF-Dokumente (Logo, Farbe, Schrift, Textbausteine).
        path: 'organisationseinstellungen/dokument-branding',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/dokument-branding/dokument-branding-page.component').then(
            (m) => m.DokumentBrandingPageComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/einladungen',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/einladungen/einladungen-list.component').then((m) => m.EinladungenListComponent),
      },
      {
        path: 'systemeinstellungen/mandanten',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/mandanten/mandanten-list.component').then((m) => m.MandantenListComponent),
      },
      // UI-Katalog (System-Admin only) — interne Component-Showcase.
      {
        path: 'systemeinstellungen/ui-katalog',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-overview.component').then((m) => m.UiKatalogOverviewComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/buttons',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-buttons.component').then((m) => m.UiKatalogButtonsComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/cards',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-cards.component').then((m) => m.UiKatalogCardsComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/dialoge',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-dialoge.component').then((m) => m.UiKatalogDialogeComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/toasts',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-toasts.component').then((m) => m.UiKatalogToastsComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/forms',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-forms.component').then((m) => m.UiKatalogFormsComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/status',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-status.component').then((m) => m.UiKatalogStatusComponent),
      },
      {
        path: 'systemeinstellungen/ui-katalog/menuestrukturen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/ui-katalog/ui-katalog-menuestrukturen.component').then(
            (m) => m.UiKatalogMenuestrukturenComponent,
          ),
      },
      {
        path: 'systemeinstellungen/mandanten/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/admin/mandanten/mandant-detail.component').then((m) => m.MandantDetailComponent),
      },
      // DMS-System-Admin Routes (W12-T11)
      {
        path: 'systemeinstellungen/dms',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/dms-admin-overview-page.component').then(
            (m) => m.DmsAdminOverviewPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/mandanten',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/mandanten/dms-mandanten-cross-page.component').then(
            (m) => m.DmsMandantenCrossPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/storage-pools',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/storage-pools/dms-storage-pools-list-page.component').then(
            (m) => m.DmsStoragePoolsListPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/storage-pools/new',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/storage-pools/dms-storage-pool-new-page.component').then(
            (m) => m.DmsStoragePoolNewPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/storage-pools/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/storage-pools/dms-storage-pool-detail-page.component').then(
            (m) => m.DmsStoragePoolDetailPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/storage-migrationen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/storage-migrationen/dms-storage-migrationen-page.component').then(
            (m) => m.DmsStorageMigrationenPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/audit-logs',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/audit-logs/dms-audit-logs-page.component').then(
            (m) => m.DmsAuditLogsPageComponent,
          ),
        children: [
          { path: '', redirectTo: 'auth', pathMatch: 'full' },
          {
            path: 'auth',
            loadComponent: () =>
              import('./views/systemeinstellungen/dms/audit-logs/dms-audit-logs-auth-tab.component').then(
                (m) => m.DmsAuditLogsAuthTabComponent,
              ),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('./views/systemeinstellungen/dms/audit-logs/dms-audit-logs-doc-tab.component').then(
                (m) => m.DmsAuditLogsDocTabComponent,
              ),
          },
        ],
      },
      {
        path: 'systemeinstellungen/dms/master-key',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/master-key/dms-master-key-page.component').then(
            (m) => m.DmsMasterKeyPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/ocr-diagnose',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/ocr-diagnose/ocr-diagnose-page.component').then(
            (m) => m.OcrDiagnosePageComponent,
          ),
      },
      // DMS System-Typ- und Vorauswahl-Verwaltung (T3.6)
      {
        path: 'systemeinstellungen/dms/dokumenttypen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/dms-typen/system-classifications-admin.component').then(
            (m) => m.SystemClassificationsAdminComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/verknuepfungstypen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/dms-typen/system-relation-types-admin.component').then(
            (m) => m.SystemRelationTypesAdminComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/vorauswahl',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/dms-typen/system-preselect-admin.component').then(
            (m) => m.SystemPreselectAdminComponent,
          ),
      },
      {
        path: 'systemeinstellungen/dms/vorgeschlagene-dokumenttypen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/dms/dms-typen/suggested-classifications-admin.component').then(
            (m) => m.SuggestedClassificationsAdminComponent,
          ),
      },
      // KI-Prozessvisualisierung (DMS-Ingestion) — Kopie der read-only Pipeline-Visualisierung
      // für den DMS-Ingestion-Prozess (gleiche Komponente wie unter ki-pruefkriterien/prozessvisualisierung,
      // fest verdrahtet auf den DMS-Prozess-Key via Route-`data`).
      {
        path: 'systemeinstellungen/dms/prozessvisualisierung',
        canActivate: [systemAdminGuard],
        data: { prozessKey: 'dms_ingestion_mistral' },
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-prozesse.component').then((m) => m.KiProzesseComponent),
      },
      // KI-Prüfkriterien: Suchprofile + Korpus + Kategorien (OLD-Urteils-Anbindung; System-Admin).
      // Components liegen weiterhin physisch unter views/systemeinstellungen/urteile/.
      {
        path: 'systemeinstellungen/ki-pruefkriterien/suchprofile',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/urteile/urteil-profil-list.component').then(
            (m) => m.UrteilProfilListComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/suchprofile/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/urteile/urteil-profil-detail.component').then(
            (m) => m.UrteilProfilDetailComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/korpus',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/urteile/urteil-korpus-list.component').then(
            (m) => m.UrteilKorpusListComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/korpus/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/urteile/urteil-detail.component').then((m) => m.UrteilDetailComponent),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/kategorien',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/urteile/urteil-kategorien-list.component').then(
            (m) => m.UrteilKategorienListComponent,
          ),
      },
      // Alt-URL-Redirects (Bookmarks/Alt-Links → neue KI-Prüfkriterien-Pfade).
      {
        path: 'systemeinstellungen/urteile/profile',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/suchprofile',
        pathMatch: 'full',
      },
      {
        path: 'systemeinstellungen/urteile/profile/:id',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/suchprofile/:id',
      },
      {
        path: 'systemeinstellungen/urteile/korpus',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/korpus',
        pathMatch: 'full',
      },
      {
        path: 'systemeinstellungen/urteile/korpus/:id',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/korpus/:id',
      },
      {
        path: 'systemeinstellungen/urteile/kategorien',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/kategorien',
        pathMatch: 'full',
      },
      { path: 'systemeinstellungen/urteile', redirectTo: 'systemeinstellungen/ki-pruefkriterien', pathMatch: 'full' },
      // KI-Infrastruktur-Modul (System-Admin)
      {
        path: 'systemeinstellungen/ki',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-infrastruktur-overview.component').then(
            (m) => m.KiInfrastrukturOverviewComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki/verbindungen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-verbindungen.component').then((m) => m.KiVerbindungenComponent),
      },
      // MUSS vor `:id` stehen (sonst matcht `neu` als id)!
      {
        path: 'systemeinstellungen/ki/verbindungen/neu',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-verbindung-neu.component').then((m) => m.KiVerbindungNeuComponent),
      },
      {
        path: 'systemeinstellungen/ki/verbindungen/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-verbindung-detail.component').then(
            (m) => m.KiVerbindungDetailComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki/avatare',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-avatare.component').then((m) => m.KiAvatareComponent),
      },
      // MUSS vor `:id` stehen (sonst matcht `neu` als id)!
      {
        path: 'systemeinstellungen/ki/avatare/neu',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-avatar-neu.component').then((m) => m.KiAvatarNeuComponent),
      },
      {
        path: 'systemeinstellungen/ki/avatare/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-avatar-detail.component').then((m) => m.KiAvatarDetailComponent),
      },
      {
        path: 'systemeinstellungen/ki/abteilungen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-abteilungen.component').then((m) => m.KiAbteilungenComponent),
      },
      {
        path: 'systemeinstellungen/ki/tarife',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-tarife.component').then((m) => m.KiTarifeComponent),
      },
      {
        path: 'systemeinstellungen/ki/kosten',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-kosten.component').then((m) => m.KiKostenComponent),
      },
      // Alt-URL-Redirect (Bookmarks) — KI-Prozesse-Visualisierung zog 2026-07-12 ins
      // KI-Prüfkriterien-Untermenü (siehe Route weiter unten).
      {
        path: 'systemeinstellungen/ki/prozesse',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/prozessvisualisierung',
        pathMatch: 'full',
      },
      // Alt-URL-Redirect (Bookmarks) — Routen-Segment 'prozesse' → 'prozessvisualisierung'
      // umbenannt (2026-07-13) zur Konsistenz mit der DMS-Prozessvisualisierung.
      {
        path: 'systemeinstellungen/ki-pruefkriterien/prozesse',
        redirectTo: 'systemeinstellungen/ki-pruefkriterien/prozessvisualisierung',
        pathMatch: 'full',
      },
      // KI-Prüfkriterien-Modul (L2) — Katalog/Detail für Kuratoren ODER System-Admins,
      // Aufträge/Einstellungen nur System-Admin.
      {
        path: 'systemeinstellungen/ki-pruefkriterien',
        canActivate: [kuratorOderAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/ki-pruefkriterien-overview.component').then(
            (m) => m.KiPruefkriterienOverviewComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/konfiguration',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/ki-pruefkriterien-konfiguration-overview.component').then(
            (m) => m.KiPruefkriterienKonfigurationOverviewComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/katalog',
        canActivate: [kuratorOderAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/katalog-list.component').then(
            (m) => m.KatalogListComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/katalog/:id',
        canActivate: [kuratorOderAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/kriterium-detail.component').then(
            (m) => m.KriteriumDetailComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/auftraege',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/auftraege-list.component').then(
            (m) => m.AuftraegeListComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/auftraege/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/auftrag-detail.component').then(
            (m) => m.AuftragDetailComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/einstellungen',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/ki-pruefkriterien/einstellungen.component').then(
            (m) => m.EinstellungenComponent,
          ),
      },
      {
        path: 'systemeinstellungen/ki-pruefkriterien/prozessvisualisierung',
        canActivate: [systemAdminGuard],
        data: { prozessKey: 'pruefkriterien' },
        loadComponent: () =>
          import('./views/systemeinstellungen/ki/ki-prozesse.component').then((m) => m.KiProzesseComponent),
      },
      // Systemdiagnose — Umgebungs-Check-Shell mit Tabs (Task A9)
      {
        path: 'systemeinstellungen/systemdiagnose',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/systemdiagnose/systemdiagnose-layout.component').then(
            (m) => m.SystemdiagnoseLayoutComponent,
          ),
        children: [
          { path: '', redirectTo: 'umgebungscheck', pathMatch: 'full' },
          {
            path: 'umgebungscheck',
            loadComponent: () =>
              import('./views/systemeinstellungen/systemdiagnose/umgebungscheck-tab.component').then(
                (m) => m.UmgebungscheckTabComponent,
              ),
          },
          {
            path: 'logs',
            loadComponent: () =>
              import('./views/systemeinstellungen/systemdiagnose/logs-tab.component').then(
                (m) => m.SystemdiagnoseLogsTabComponent,
              ),
          },
          {
            path: 'worker-queues',
            loadComponent: () =>
              import('./views/systemeinstellungen/systemdiagnose/worker-queues-tab.component').then(
                (m) => m.WorkerQueuesTabComponent,
              ),
          },
          {
            path: 'assistent',
            loadComponent: () =>
              import('./views/systemeinstellungen/systemdiagnose/assistent-tab.component').then(
                (m) => m.SystemdiagnoseAssistentTabComponent,
              ),
          },
        ],
      },
      {
        path: 'systemeinstellungen/wartungsmodus',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/wartungsmodus/wartungsmodus-page.component').then(
            (m) => m.WartungsmodusPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/system-admins',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/system-admins/system-admins-page.component').then(
            (m) => m.SystemAdminsPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/scheduler',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/scheduler/scheduler-jobs-page.component').then(
            (m) => m.SchedulerJobsPageComponent,
          ),
      },
      {
        path: 'systemeinstellungen/scheduler/:id',
        canActivate: [systemAdminGuard],
        loadComponent: () =>
          import('./views/systemeinstellungen/scheduler/scheduler-job-detail-page.component').then(
            (m) => m.SchedulerJobDetailPageComponent,
          ),
      },
      {
        // Globale Wissensdatenbank (KI-Wissensbasis, Trägermandant) — Liste (System-Admin).
        path: 'systemeinstellungen/wissensdatenbank',
        canActivate: [systemAdminGuard],
        data: { scope: 'global' },
        loadComponent: () =>
          import('./views/wissensdatenbank/wissensdatenbank-list-page.component').then(
            (m) => m.WissensdatenbankListPageComponent,
          ),
      },
      {
        // Globale Wissensdatenbank — Detail (System-Admin).
        path: 'systemeinstellungen/wissensdatenbank/:id',
        canActivate: [systemAdminGuard],
        data: { scope: 'global' },
        loadComponent: () =>
          import('./views/wissensdatenbank/wissensdatenbank-detail-page.component').then(
            (m) => m.WissensdatenbankDetailPageComponent,
          ),
      },
      {
        // "Mein Mandant" — Mandanten-Admin bearbeitet den eigenen Mandanten.
        // Die MandantDetailComponent liest data.meinMandant und lädt via AuthService.
        path: 'organisationseinstellungen/mein-mandant',
        canActivate: [mandantAdminGuard],
        data: { meinMandant: true },
        loadComponent: () =>
          import('./views/admin/mandanten/mandant-detail.component').then((m) => m.MandantDetailComponent),
      },
      {
        path: 'organisationseinstellungen/business-units',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/business-units/business-units-list.component').then(
            (m) => m.BusinessUnitsListComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/business-units/neu',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/business-units/business-unit-detail.component').then(
            (m) => m.BusinessUnitDetailComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/business-units/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/business-units/business-unit-detail.component').then(
            (m) => m.BusinessUnitDetailComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/benutzer',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/benutzer/benutzer-list.component').then((m) => m.BenutzerListComponent),
      },
      {
        path: 'organisationseinstellungen/benutzer/neu',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/benutzer/benutzer-detail.component').then((m) => m.BenutzerDetailComponent),
      },
      {
        path: 'organisationseinstellungen/benutzer/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/benutzer/benutzer-detail.component').then((m) => m.BenutzerDetailComponent),
      },
      {
        path: 'organisationseinstellungen/rollen',
        canActivate: [mandantAdminGuard],
        loadComponent: () => import('./views/admin/rollen/rollen-list.component').then((m) => m.RollenListComponent),
      },
      {
        path: 'organisationseinstellungen/rollen/neu',
        canActivate: [mandantAdminGuard],
        loadComponent: () => import('./views/admin/rollen/rolle-detail.component').then((m) => m.RolleDetailComponent),
      },
      {
        path: 'organisationseinstellungen/rollen/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () => import('./views/admin/rollen/rolle-detail.component').then((m) => m.RolleDetailComponent),
      },
      {
        path: 'organisationseinstellungen/honorartafeln',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/honorartafeln/honorartafeln-admin-list.component').then(
            (m) => m.HonorartafelnAdminListComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/honorartafeln/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/honorartafeln/honorartafel-admin-detail.component').then(
            (m) => m.HonorartafelAdminDetailComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/mvb-kennwerte',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/mvb-kennwerte/mvb-kennwerte-admin-list.component').then(
            (m) => m.MvbKennwerteAdminListComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/nhk-rechner',
        canActivate: [mandantAdminGuard],
        loadComponent: () => import('./views/admin/nhk-config/nhk-config.component').then((m) => m.NhkConfigComponent),
      },
      {
        path: 'organisationseinstellungen/besondere-leistungen',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/besondere-leistungen/besondere-leistungen-admin-list.component').then(
            (m) => m.BesondereLeistungenAdminListComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/besondere-leistungen/:anlage',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/besondere-leistungen/besondere-leistungen-admin-detail.component').then(
            (m) => m.BesondereLeistungenAdminDetailComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/leistungsbilder',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/leistungsbilder/leistungsbilder-admin-list.component').then(
            (m) => m.LeistungsbilderAdminListComponent,
          ),
      },
      {
        // DMS-Konfiguration: Dokumenttypen (Klassifikationen) verwalten.
        path: 'organisationseinstellungen/dms-konfiguration/dokumenttypen',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/dms-konfiguration/classifications-admin.component').then(
            (m) => m.ClassificationsAdminComponent,
          ),
      },
      {
        // DMS-Konfiguration: Verknüpfungstypen verwalten.
        path: 'organisationseinstellungen/dms-konfiguration/verknuepfungstypen',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/dms-konfiguration/relation-types-admin.component').then(
            (m) => m.RelationTypesAdminComponent,
          ),
      },
      {
        // DMS-Konfiguration: Verknüpfungstyp-Vorauswahl je Entität pflegen.
        path: 'organisationseinstellungen/dms-konfiguration/vorauswahl',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/dms-konfiguration/preselect-admin.component').then(
            (m) => m.PreselectAdminComponent,
          ),
      },
      {
        path: 'organisationseinstellungen/leistungsbilder/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/admin/leistungsbilder/leistungsbild-admin-detail.component').then(
            (m) => m.LeistungsbildAdminDetailComponent,
          ),
      },
      {
        // Liegenschaften-Tree-Seite (Bestands-Baum: Liegenschaften, Gebäude, Ebenen, Räume).
        path: 'organisationseinstellungen/liegenschaften',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/portal/liegenschaften/liegenschaften.component').then((m) => m.LiegenschaftenComponent),
      },
      {
        // Liegenschaft/Bestandsknoten-Detail (Placeholder — Task 3b: typ-Card + DMS-Tab).
        path: 'organisationseinstellungen/liegenschaften/:id',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/portal/liegenschaften/liegenschaft-detail.component').then(
            (m) => m.LiegenschaftDetailComponent,
          ),
      },
      {
        // Projekt-Standards: Standard-Projektart für neue Projekte (Phase 6 / Task 5).
        path: 'organisationseinstellungen/projekt-defaults',
        canActivate: [mandantAdminGuard],
        loadComponent: () =>
          import('./views/organisationseinstellungen/projekt-defaults/projekt-defaults.component').then(
            (m) => m.ProjektDefaultsComponent,
          ),
      },
      {
        // Mandanten-Wissensdatenbank (KI-Wissensbasis) — Liste (Mandant-Admin).
        path: 'organisationseinstellungen/wissensdatenbank',
        canActivate: [mandantAdminGuard],
        data: { scope: 'mandant' },
        loadComponent: () =>
          import('./views/wissensdatenbank/wissensdatenbank-list-page.component').then(
            (m) => m.WissensdatenbankListPageComponent,
          ),
      },
      {
        // Mandanten-Wissensdatenbank — Detail (Mandant-Admin).
        path: 'organisationseinstellungen/wissensdatenbank/:id',
        canActivate: [mandantAdminGuard],
        data: { scope: 'mandant' },
        loadComponent: () =>
          import('./views/wissensdatenbank/wissensdatenbank-detail-page.component').then(
            (m) => m.WissensdatenbankDetailPageComponent,
          ),
      },
      // Portal
      {
        path: 'projekte',
        loadComponent: () =>
          import('./views/portal/projekte/projekte-list.component').then((m) => m.ProjekteListComponent),
      },
      {
        path: 'projekte/neu',
        loadComponent: () =>
          import('./views/portal/projekte/projekt-detail.component').then((m) => m.ProjektDetailComponent),
      },
      {
        // Kostenhistorie — Legacy-Redirect auf neuen Kostenüberblick.
        path: 'projekte/:id/kostenhistorie',
        redirectTo: 'projekte/:id/kosten/ueberblick',
        pathMatch: 'full',
      },
      {
        // Wizard — eigene Seite ohne Tab-Layout (wie Kostenhistorie). Muss VOR 'projekte/:id' stehen.
        path: 'projekte/:id/vertraege/:vertragId/wizard',
        loadComponent: () => import('./views/portal/vertraege/wizard/wizard.component').then((m) => m.WizardComponent),
      },
      {
        // Leistungsbild-Detail — Stammdaten ansehen/bearbeiten + Wizard- / Leistungsfeststellungs-Einstieg.
        path: 'projekte/:id/vertraege/:vertragId/leistungsbild/:leistungsbildId',
        loadComponent: () =>
          import('./views/portal/vertraege/leistungsbild-detail.component').then((m) => m.LeistungsbildDetailComponent),
      },
      {
        // Leistungsstandfeststellungen — Liste pro Leistungsbild.
        path: 'projekte/:id/vertraege/:vertragId/leistungsbild/:leistungsbildId/feststellungen',
        loadComponent: () =>
          import('./views/portal/vertraege/leistungsstand/leistungsstand-list.component').then(
            (m) => m.LeistungsstandListComponent,
          ),
      },
      {
        // Wizard — Teilleistungs-genaue Erfassung mit Hilfetexten und TGA-Anlagengruppen.
        path: 'projekte/:id/vertraege/:vertragId/leistungsbild/:leistungsbildId/feststellungen/neu-wizard',
        loadComponent: () =>
          import('./views/portal/vertraege/leistungsstand/leistungsstand-wizard.component').then(
            (m) => m.LeistungsstandWizardComponent,
          ),
      },
      {
        // Alter Anlage-Pfad. Der Assistent ist seit 2026-08-17 der einzige Weg,
        // eine Feststellung anzulegen: Das frühere Formular kannte nur
        // Phasen-Positionen und hätte Teilleistungen wie Zusatzleistungen
        // stillschweigend auf 0 % gesetzt. Die Route bleibt als Umleitung
        // bestehen, damit gespeicherte Lesezeichen nicht in einen 404 laufen.
        path: 'projekte/:id/vertraege/:vertragId/leistungsbild/:leistungsbildId/feststellungen/neu',
        redirectTo: ({ params }) =>
          `/projekte/${params['id']}/vertraege/${params['vertragId']}` +
          `/leistungsbild/${params['leistungsbildId']}/feststellungen/neu-wizard`,
      },
      {
        path: 'projekte/:id/vertraege/:vertragId/leistungsbild/:leistungsbildId/feststellungen/:feststellungId',
        loadComponent: () =>
          import('./views/portal/vertraege/leistungsstand/leistungsstand-detail.component').then(
            (m) => m.LeistungsstandDetailComponent,
          ),
      },
      {
        path: 'projekte/:id',
        loadComponent: () =>
          import('./views/portal/projekte/projekt-detail-layout.component').then((m) => m.ProjektDetailLayoutComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./views/portal/projekte/projekt-detail.component').then((m) => m.ProjektDetailComponent),
          },
          {
            path: 'vertraege',
            loadComponent: () =>
              import('./views/portal/vertraege/vertrag-list.component').then((m) => m.VertragListComponent),
          },
          {
            path: 'vertraege/neu',
            loadComponent: () =>
              import('./views/portal/vertraege/vertrag-detail.component').then((m) => m.VertragDetailComponent),
          },
          {
            // Vertrag-Detail als Tab-Layout: Stammdaten / Leistungsbild / Honorartafel.
            path: 'vertraege/:vertragId',
            loadComponent: () =>
              import('./views/portal/vertraege/vertrag-detail-layout.component').then(
                (m) => m.VertragDetailLayoutComponent,
              ),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-detail.component').then((m) => m.VertragDetailComponent),
              },
              {
                path: 'leistungsbild',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-leistungsbilder.component').then(
                    (m) => m.VertragLeistungsbilderComponent,
                  ),
              },
              {
                path: 'honorarparameter',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-honorarparameter.component').then(
                    (m) => m.VertragHonorarparameterComponent,
                  ),
              },
              {
                path: 'dokumente',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-dokumente-tab.component').then(
                    (m) => m.VertragDokumenteTabComponent,
                  ),
              },
              {
                path: 'honorartafel',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-honorartafel.component').then(
                    (m) => m.VertragHonorartafelComponent,
                  ),
              },
              {
                path: 'honorar',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-honorar.component').then((m) => m.VertragHonorarComponent),
              },
              {
                path: 'berechnungsgrundlagen',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-berechnungsgrundlagen.component').then(
                    (m) => m.VertragBerechnungsgrundlagenComponent,
                  ),
              },
              {
                path: 'stundenabrechnung',
                loadComponent: () =>
                  import('./views/portal/vertraege/stundenabrechnung/vertrag-stundenabrechnung.component').then(
                    (m) => m.VertragStundenabrechnungComponent,
                  ),
              },
              {
                path: 'stundenabrechnung/:nachweisId',
                loadComponent: () =>
                  import('./views/portal/vertraege/stundenabrechnung/stundennachweis-detail.component').then(
                    (m) => m.StundennachweisDetailComponent,
                  ),
              },
              {
                path: 'historie',
                loadComponent: () =>
                  import('./views/portal/vertraege/vertrag-historie.component').then((m) => m.VertragHistorieComponent),
              },
            ],
          },
          // Kosten-Bereich (neu — ersetzt Kostenermittlungen)
          {
            path: 'kosten',
            loadComponent: () =>
              import('./views/portal/kosten/kosten-layout.component').then((m) => m.KostenLayoutComponent),
            children: [
              {
                path: '',
                redirectTo: 'ueberblick',
                pathMatch: 'full',
              },
              {
                path: 'ueberblick',
                loadComponent: () =>
                  import('./views/portal/kosten/kostenueberblick-page.component').then(
                    (m) => m.KostenueberblickPageComponent,
                  ),
              },
              {
                path: 'erfassungen',
                loadComponent: () =>
                  import('./views/portal/kosten/kostenerfassungs-list.component').then(
                    (m) => m.KostenerfassungsListComponent,
                  ),
              },
              {
                path: 'stufen',
                loadComponent: () =>
                  import('./views/portal/kosten/projekt-stufen-aktivierung.component').then(
                    (m) => m.ProjektStufenAktivierungComponent,
                  ),
              },
            ],
          },
          {
            path: 'dokumente',
            loadComponent: () =>
              import('./views/portal/projekte/projekt-dokumente-tab.component').then(
                (m) => m.ProjektDokumenteTabComponent,
              ),
          },
          {
            path: 'historie',
            loadComponent: () =>
              import('./views/portal/projekte/projekt-historie.component').then((m) => m.ProjektHistorieComponent),
          },
          {
            path: 'ki-projektleiter',
            loadComponent: () =>
              import('./views/portal/projekte/ki-projektleiter/projekt-ki-projektleiter-tab.component').then(
                (m) => m.ProjektKiProjektleiterTabComponent,
              ),
          },
          {
            path: 'mvb',
            loadComponent: () => import('./views/portal/projekte/mvb/mvb-tab.component').then((m) => m.MvbTabComponent),
          },
          // Legacy-Redirects von alter Kostenermittlungs-URL
          {
            path: 'kostenermittlungen',
            redirectTo: 'kosten/erfassungen',
            pathMatch: 'full',
          },
          {
            path: 'kostenermittlungen/neu',
            redirectTo: 'kosten/erfassungen',
            pathMatch: 'full',
          },
          {
            path: 'kostenermittlungen/:keId',
            redirectTo: 'kosten/erfassungen',
            pathMatch: 'full',
          },
          {
            path: 'kostenermittlungen/:keId/ansicht',
            redirectTo: 'kosten/erfassungen',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'organisationen',
        loadComponent: () =>
          import('./views/portal/organisationen/organisationen-list.component').then(
            (m) => m.OrganisationenListComponent,
          ),
      },
      {
        path: 'organisationen/neu',
        loadComponent: () =>
          import('./views/portal/organisationen/organisation-detail.component').then(
            (m) => m.OrganisationDetailComponent,
          ),
      },
      {
        path: 'organisationen/:id',
        loadComponent: () =>
          import('./views/portal/organisationen/organisation-detail.component').then(
            (m) => m.OrganisationDetailComponent,
          ),
      },
      // Legacy-URLs: 301-Redirect für Bookmarks
      { path: 'externe-firmen', redirectTo: 'organisationen', pathMatch: 'full' },
      { path: 'externe-firmen/neu', redirectTo: 'organisationen/neu', pathMatch: 'full' },
      { path: 'externe-firmen/:id', redirectTo: 'organisationen/:id' },
      // Ansprechpartner-Hauptmenü: mandantenweite Liste + Detail mit Org-Zuordnungs-Historie
      {
        path: 'ansprechpartner',
        loadComponent: () =>
          import('./views/portal/ansprechpartner/ansprechpartner-list.component').then(
            (m) => m.AnsprechpartnerListComponent,
          ),
      },
      {
        path: 'ansprechpartner/:id',
        loadComponent: () =>
          import('./views/portal/ansprechpartner/ansprechpartner-detail.component').then(
            (m) => m.AnsprechpartnerDetailComponent,
          ),
      },
      // Honorartafeln
      {
        path: 'honorartafeln',
        loadComponent: () =>
          import('./views/portal/honorartafeln/honorartafel-list.component').then((m) => m.HonorartafelListComponent),
      },
      {
        path: 'honorartafeln/:id',
        loadComponent: () =>
          import('./views/portal/honorartafeln/honorartafel-detail.component').then(
            (m) => m.HonorartafelDetailComponent,
          ),
      },
      // W7-T13: DMS-Dokumenten-Übersicht (Skelett — vollständige Documents-Table in W8).
      {
        path: 'dokumente',
        loadComponent: () =>
          import('./views/dms/pages/dms-list-page/dms-list-page.component').then((m) => m.DmsListPageComponent),
      },
      // W8-T13: DMS-Document-Detail Layout mit Lazy-Loaded Child-Tabs.
      {
        path: 'dokumente/:id',
        loadComponent: () =>
          import('./views/dms/pages/dms-document-detail-layout/dms-document-detail-layout.component').then(
            (m) => m.DmsDocumentDetailLayoutComponent,
          ),
        children: [
          { path: '', redirectTo: 'stammdaten', pathMatch: 'full' },
          {
            path: 'stammdaten',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/stammdaten-tab/dms-document-stammdaten-tab.component').then(
                (m) => m.DmsDocumentStammdatenTabComponent,
              ),
          },
          {
            path: 'versionen',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/versionen-tab/dms-document-versionen-tab.component').then(
                (m) => m.DmsDocumentVersionenTabComponent,
              ),
          },
          {
            path: 'verknuepfungen',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/verknuepfungen-tab/dms-document-verknuepfungen-tab.component').then(
                (m) => m.DmsDocumentVerknuepfungenTabComponent,
              ),
          },
          {
            path: 'workflow',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/workflow-tab/dms-document-workflow-tab.component').then(
                (m) => m.DmsDocumentWorkflowTabComponent,
              ),
          },
          {
            path: 'audit',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/audit-tab/dms-document-audit-tab.component').then(
                (m) => m.DmsDocumentAuditTabComponent,
              ),
          },
          {
            path: 'ocr-text',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/ocr-text-tab/dms-document-ocr-text-tab.component').then(
                (m) => m.DmsDocumentOcrTextTabComponent,
              ),
          },
          // OCR-Prüfung wurde in den Chunks-Tab konsolidiert (K1, 2026-07-20).
          // Redirect-Alias, damit Bookmarks/alte Links nicht 404en.
          { path: 'ocr-pruefung', redirectTo: 'chunks', pathMatch: 'full' },
          {
            path: 'chunks',
            loadComponent: () =>
              import('./views/dms/pages/dms-document-detail-layout/tabs/chunks-tab/dms-document-chunks-tab.component').then(
                (m) => m.DmsDocumentChunksTabComponent,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', component: T2dErrorPageComponent, data: { errorCode: 404 } },
];
